/**
 * @file FilePreviewList.tsx
 * @description Component to display file previews BEFORE sending message and during upload.
 *
 * Selection phase:
 * - Instant client-side thumbnail for images (Claude-like UX)
 * - Remove button per file (calls onRemoveFile)
 * - Inline warning under large files (PDF > 5MB / others > 3MB)
 *
 * Upload phase (uploadingBatch active):
 * - 3px bottom progress bar 0→100% (primary on uploading, error on failure, success on complete)
 * - % numeric overlay replaces size badge during upload
 * - Thumbnail dimmed to 70% opacity
 * - X cancels the XHR (Esc keyboard works too via ChatInput)
 * - Success: brief green tick before parent unmounts the card
 * - Error: red bar + alert icon + dismiss action
 *
 * @version 2.0.0 (Phase 1 chat upload feedback — Plan #53)
 */

import React from 'react';
import {
  Box,
  IconButton,
  LinearProgress,
  Tooltip,
  Skeleton,
  Typography,
  styled,
  useTheme,
  Theme,
  alpha,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Close as CloseIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  TableChart as SpreadsheetIcon,
  Code as CodeIcon,
  Folder as ZipIcon,
  InsertDriveFile as FileIcon,
  Check as CheckIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { formatFileSize } from '../../utils/thumbnailGenerator';
import type { FileWithThumbnail } from '../../utils/thumbnailGenerator';

/**
 * Snapshot of an in-flight (or recently-finished) upload batch.
 *
 * Lives in `useMessageSending`; the cards in this list mirror its state until the
 * mutation resolves. After success there is a 300 ms tick window during which
 * `status === 'success'` before the parent clears the batch.
 */
export interface UploadingBatch {
  /** Files included in this upload, in submission order. */
  items: FileWithThumbnail[];
  /** Aggregate transfer progress 0-100 (XMLHttpRequest reports total bytes). */
  progress: number;
  /** Lifecycle of the batch. */
  status: 'uploading' | 'success' | 'error' | 'cancelled';
  /** Server-side / network error message when status === 'error'. */
  error: string | null;
  /** AbortController bound to the XHR — abort to cancel. */
  controller: AbortController | null;
}

/**
 * Props for FilePreviewList component
 */
export interface FilePreviewListProps {
  /** Files with their generated thumbnails (selection phase). */
  filesWithThumbnails: FileWithThumbnail[];
  /** Remove file from the selection (selection phase only). */
  onRemoveFile: (file: File) => void;
  /** Indicates if thumbnails are being generated. */
  isGeneratingThumbnails?: boolean;
  /** Active upload batch — when present, takes over the preview rendering. */
  uploadingBatch?: UploadingBatch | null;
  /** Cancel the in-flight XHR (X button on card, Esc keyboard). */
  onCancelUpload?: () => void;
  /** Dismiss an error batch without aborting (already finished). */
  onDismissUploadError?: () => void;
}

/**
 * Styled components
 */
const PreviewContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1),
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const PreviewCard = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: 96,
  height: 96,
  borderRadius: theme.spacing(0.75),
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  cursor: 'pointer',
  transition: 'all 0.2s ease',

  '&:hover': {
    borderColor: theme.palette.primary.main,
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const ThumbnailImage = styled('img')<{ dimmed?: boolean }>(({ dimmed }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  opacity: dimmed ? 0.7 : 1,
  transition: 'opacity 0.2s ease',
}));

const IconContainer = styled(Box)<{ dimmed?: boolean }>(({ theme, dimmed }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(1),
  opacity: dimmed ? 0.7 : 1,
  transition: 'opacity 0.2s ease',
}));

const RemoveButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 4,
  right: 4,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[2],
  padding: theme.spacing(0.5),
  zIndex: 10,

  '&:hover': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
}));

const FileSizeBadge = styled(Box)(({ theme }) => {
  const fileIconColors = (theme as any).fileIcons;

  return {
    position: 'absolute',
    bottom: 2,
    left: 2,
    backgroundColor: fileIconColors?.sizeBadge?.background ?? 'rgba(0, 0, 0, 0.92)',
    color: '#FFFFFF',
    padding: theme.spacing(0.125, 0.5),
    borderRadius: theme.spacing(0.25),
    fontSize: '0.55rem',
    fontWeight: 700,
    textShadow: '0 1px 3px rgba(0, 0, 0, 0.9)',
    letterSpacing: '0.02em',
    border: '0.5px solid rgba(0, 0, 0, 0.2)',
    zIndex: 5,
  };
});

const ProgressBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 2,
  left: 2,
  backgroundColor: alpha(theme.palette.primary.main, 0.92),
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(0.125, 0.5),
  borderRadius: theme.spacing(0.25),
  fontSize: '0.6rem',
  fontWeight: 700,
  letterSpacing: '0.02em',
  zIndex: 6,
}));

// Small "outline" status badge that lives in the top-left corner of the card.
// Pattern borrowed from Notion / Linear / Claude attachments: paper background +
// thin colored border + colored icon, never a saturated fill that fights the
// thumbnail. Sits below the RemoveButton (top-right) so neither obscures the
// other.
const CornerStatusBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 4,
  left: 4,
  width: 18,
  height: 18,
  borderRadius: '50%',
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.16)}`,
  pointerEvents: 'none',
  zIndex: 7,
  // Soft entrance: slight overshoot, ~220 ms, no bounce.
  animation: 'preview-status-in 220ms cubic-bezier(0.34, 1.4, 0.64, 1) both',
  '@keyframes preview-status-in': {
    '0%': { opacity: 0, transform: 'scale(0.55)' },
    '100%': { opacity: 1, transform: 'scale(1)' },
  },
}));

/**
 * Get appropriate icon for file type
 * Uses semantic tokens from theme for consistent colors
 */
function getFileIcon(mimeType: string, theme: Theme): React.ReactElement {
  const type = mimeType.toLowerCase();
  const fileIconColors = (theme as any).fileIcons;

  if (type.includes('pdf')) {
    return <PdfIcon sx={{ fontSize: 48, color: fileIconColors?.pdf || theme.palette.error.main }} />;
  } else if (type.includes('word') || type.includes('document')) {
    return <DocIcon sx={{ fontSize: 48, color: fileIconColors?.document || theme.palette.info.main }} />;
  } else if (type.includes('sheet') || type.includes('excel')) {
    return <SpreadsheetIcon sx={{ fontSize: 48, color: fileIconColors?.spreadsheet || theme.palette.success.main }} />;
  } else if (type.includes('json') || type.includes('javascript') || type.includes('text/plain')) {
    return <CodeIcon sx={{ fontSize: 48, color: fileIconColors?.code || theme.palette.warning.main }} />;
  } else if (type.includes('zip') || type.includes('rar') || type.includes('compressed')) {
    return <ZipIcon sx={{ fontSize: 48, color: fileIconColors?.archive || theme.palette.secondary.main }} />;
  } else if (type.startsWith('image/')) {
    return <ImageIcon sx={{ fontSize: 48, color: fileIconColors?.image || theme.palette.info.main }} />;
  } else {
    return <FileIcon sx={{ fontSize: 48, color: fileIconColors?.generic || theme.palette.grey[500] }} />;
  }
}

/**
 * Threshold (bytes) above which we surface the "extracción puede tardar ~30s" warning.
 * PDFs trigger at 5MB, other types at 3MB — empirically matches Santia's reports.
 */
function shouldWarnLargeFile(file: File): boolean {
  const size = file.size;
  const isPdf = (file.type || '').toLowerCase().includes('pdf') ||
                file.name.toLowerCase().endsWith('.pdf');
  const limit = isPdf ? 5 * 1024 * 1024 : 3 * 1024 * 1024;
  return size > limit;
}

/**
 * Individual file preview component
 */
interface FilePreviewProps {
  fileWithThumbnail: FileWithThumbnail;
  onRemove: () => void;
  isGenerating?: boolean;
  // Upload phase
  uploadStatus?: UploadingBatch['status'];
  uploadProgress?: number;
  uploadError?: string | null;
  removeLabel?: string;
}

const FilePreview: React.FC<FilePreviewProps> = ({
  fileWithThumbnail,
  onRemove,
  isGenerating = false,
  uploadStatus,
  uploadProgress,
  uploadError,
  removeLabel,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { file, thumbnailDataUrl } = fileWithThumbnail;

  const isUploading = uploadStatus === 'uploading';
  const isSuccess = uploadStatus === 'success';
  const isError = uploadStatus === 'error';
  const dimmed = isUploading;

  // Progress bar color reflects status
  const barColor = isError
    ? theme.palette.error.main
    : isSuccess
      ? theme.palette.success.main
      : theme.palette.primary.main;

  const tooltipTitle = isError && uploadError
    ? `${file.name} — ${uploadError}`
    : file.name;

  return (
    <PreviewCard
      sx={isError ? { borderColor: theme.palette.error.main } : undefined}
    >
      {/* Remove / cancel button */}
      <Tooltip title={removeLabel || t('chat.removeFileTooltip')}>
        <RemoveButton
          size="small"
          aria-label={removeLabel || t('chat.removeFileTooltip')}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <CloseIcon fontSize="small" />
        </RemoveButton>
      </Tooltip>

      {/* Content */}
      <Tooltip title={tooltipTitle} arrow placement="top">
        <Box sx={{ width: '100%', height: '100%' }}>
          {isGenerating ? (
            <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
          ) : thumbnailDataUrl ? (
            <ThumbnailImage
              src={thumbnailDataUrl}
              alt={file.name}
              loading="lazy"
              dimmed={dimmed}
            />
          ) : (
            <IconContainer dimmed={dimmed}>
              {getFileIcon(file.type, theme)}
            </IconContainer>
          )}
        </Box>
      </Tooltip>

      {/* Status badges — small corner mark, NOT a saturated overlay over the thumb. */}
      {isSuccess && (
        <CornerStatusBadge
          sx={{
            border: `1.5px solid ${theme.palette.success.main}`,
            color: theme.palette.success.main,
          }}
        >
          <CheckIcon sx={{ fontSize: '0.78rem' }} />
        </CornerStatusBadge>
      )}

      {isError && (
        <CornerStatusBadge
          sx={{
            border: `1.5px solid ${theme.palette.error.main}`,
            color: theme.palette.error.main,
          }}
        >
          <ErrorIcon sx={{ fontSize: '0.78rem' }} />
        </CornerStatusBadge>
      )}

      {/* Bottom badge: % during upload, "Error" on failure, size badge otherwise */}
      {!isGenerating && (
        isUploading ? (
          <ProgressBadge>
            {Math.max(0, Math.min(100, Math.round(uploadProgress ?? 0)))}%
          </ProgressBadge>
        ) : isError ? (
          <ProgressBadge sx={{ backgroundColor: (t) => alpha(t.palette.error.main, 0.92) }}>
            Error
          </ProgressBadge>
        ) : (
          <FileSizeBadge>{formatFileSize(file.size)}</FileSizeBadge>
        )
      )}

      {/* 3px progress bar pinned to the bottom edge of the card */}
      {(isUploading || isSuccess || isError) && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 3,
            zIndex: 3,
            overflow: 'hidden',
          }}
        >
          <LinearProgress
            variant="determinate"
            value={isSuccess || isError ? 100 : Math.max(0, Math.min(100, uploadProgress ?? 0))}
            sx={{
              height: 3,
              backgroundColor: alpha(barColor, 0.18),
              '& .MuiLinearProgress-bar': {
                backgroundColor: barColor,
                transition: 'transform 0.15s linear',
              },
            }}
          />
        </Box>
      )}
    </PreviewCard>
  );
};

/**
 * FilePreviewList component
 * Displays a grid of file previews — selection phase or active upload batch.
 */
export const FilePreviewList: React.FC<FilePreviewListProps> = ({
  filesWithThumbnails,
  onRemoveFile,
  isGeneratingThumbnails = false,
  uploadingBatch = null,
  onCancelUpload,
  onDismissUploadError,
}) => {
  const { t } = useTranslation();

  // The upload batch wins: while it's active the cards mirror the in-flight files,
  // not the (now-cleared) selection.
  const isUploadingActive = uploadingBatch !== null;
  const itemsToRender = isUploadingActive ? uploadingBatch!.items : filesWithThumbnails;

  if (itemsToRender.length === 0) {
    return null;
  }

  // Map each item to a card-level handler. During upload the X cancels the XHR
  // (single controller drives the whole batch); during selection it removes one
  // file from the picker.
  const handleRemove = (file: File) => {
    if (isUploadingActive && uploadingBatch) {
      if (uploadingBatch.status === 'uploading') {
        onCancelUpload?.();
      } else if (uploadingBatch.status === 'error') {
        onDismissUploadError?.();
      }
      return;
    }
    onRemoveFile(file);
  };

  const removeLabel = isUploadingActive
    ? (uploadingBatch?.status === 'uploading'
        ? t('chat.cancelUploadTooltip')
        : t('chat.dismissUploadErrorTooltip'))
    : undefined;

  // Inline warning text for large files (selection phase only — once upload starts
  // the user has already committed)
  const largeFiles = !isUploadingActive
    ? filesWithThumbnails.filter((entry) => shouldWarnLargeFile(entry.file))
    : [];

  return (
    <Box role="status" aria-live="polite" aria-atomic="true">
      <PreviewContainer>
        {itemsToRender.map((fileWithThumbnail, index) => (
          <FilePreview
            key={`${fileWithThumbnail.file.name}-${fileWithThumbnail.file.size}-${index}`}
            fileWithThumbnail={fileWithThumbnail}
            onRemove={() => handleRemove(fileWithThumbnail.file)}
            isGenerating={!isUploadingActive && isGeneratingThumbnails}
            uploadStatus={isUploadingActive ? uploadingBatch!.status : undefined}
            uploadProgress={isUploadingActive ? uploadingBatch!.progress : undefined}
            uploadError={isUploadingActive ? uploadingBatch!.error : undefined}
            removeLabel={removeLabel}
          />
        ))}
      </PreviewContainer>

      {largeFiles.length > 0 && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            px: 1,
            mt: -0.5,
            mb: 1,
            color: 'text.secondary',
            fontStyle: 'italic',
          }}
        >
          {t('chat.largeFileExtractionHint', {
            defaultValue: 'Fichero grande, la extracción puede tardar ~30s',
          })}
        </Typography>
      )}

      {isUploadingActive && uploadingBatch?.status === 'error' && uploadingBatch.error && (
        <Typography
          variant="caption"
          role="alert"
          sx={{
            display: 'block',
            px: 1,
            mt: -0.5,
            mb: 1,
            color: 'error.main',
          }}
        >
          {uploadingBatch.error}
        </Typography>
      )}
    </Box>
  );
};

export default FilePreviewList;
